import { useState, useContext, useMemo } from "react";
import BryntumContext from "@/react/context/bryntumContext";
// @ts-expect-error - userPreferences is dynamically set in bryntumContext
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import { InspectionType, CardType, AdvisorType, FieldAdvisorCardType, PromoteType, PromoteCardType } from "../constants/settingsStructure";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { ContextCard } from "@/bryntum-lib/models/ContextCard";
import { FieldAdvisorCard } from "@/bryntum-lib/models/FieldAdvisorCard";
import { PromotePromptCard } from "@/bryntum-lib/models/PromotePromptCard";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { toast } from "sonner";
import { getPublishKey } from "../utils/promptUtils";
import { useBoardPermissions } from "./useBoardPermissions";

/**
 * Configuration for prompt card management.
 * Uses discriminated union pattern for type-safe handling of different prompt types.
 *
 * The 'mode' field acts as the discriminator, determining which properties are available.
 * TypeScript will enforce that the correct fields are used together.
 *
 * @example Context cards (workmanship/site safety)
 * ```typescript
 * const config: PromptCardConfig = {
 *   mode: 'context',
 *   inspectionType: 'workmanship',
 *   cardType: 'role'
 * };
 * ```
 *
 * @example Field advisor cards (analyze/execute/review)
 * ```typescript
 * const config: PromptCardConfig = {
 *   mode: 'field-advisor',
 *   advisorType: 'analyze',
 *   fieldAdvisorCardType: 'persona'
 * };
 * ```
 *
 * @example Promote prompt cards
 * ```typescript
 * const config: PromptCardConfig = {
 *   mode: 'promote',
 *   promoteType: 'promote',
 *   promoteCardType: 'role'
 * };
 * ```
 */
export type PromptCardConfig =
  | {
      mode: 'context';
      inspectionType: InspectionType;
      cardType: CardType;
    }
  | {
      mode: 'field-advisor';
      advisorType: AdvisorType;
      fieldAdvisorCardType: FieldAdvisorCardType;
    }
  | {
      mode: 'promote';
      promoteType: PromoteType;
      promoteCardType: PromoteCardType;
    };

// Unified hook for all prompt card types
export const usePromptCards = (config: PromptCardConfig, isRequired: boolean = false) => {
  const { project } = useContext(BryntumContext);
  const [saving, setSaving] = useState(false);

  // Get publish key based on config - used to show/hide publish button
  const publishKey = config.mode === 'context'
    ? getPublishKey(config.inspectionType, undefined, undefined)
    : config.mode === 'field-advisor'
      ? getPublishKey(undefined, config.advisorType, undefined)
      : getPublishKey(undefined, undefined, config.promoteType);

  // Use shared permission hook
  const { canRead, canWrite } = useBoardPermissions();

  // Determine which store to use based on mode
  // @ts-expect-error - stores are added dynamically in projectConfig
  const store = config.mode === 'context' ? project?.contextCardsStore : config.mode === 'field-advisor' ? project?.fieldAdvisorCardsStore : project?.promotePromptCardStore;

  // Determine store key for subscription
  const storeKey = config.mode === 'context' ? STORE_KEYS.contextCardsStore : config.mode === 'field-advisor' ? STORE_KEYS.fieldAdvisorCardsStore : STORE_KEYS.promotePromptCardStore;

  // Subscribe to store changes - triggers rerender automatically
  const { version, forceRerender } = useSwivuStore<ContextCard | FieldAdvisorCard | PromotePromptCard>({
    storeId: storeKey
  });

  // Get cards from store - filter by type and card type
  const cards = useMemo(() => {
    if (!store) return [];

    if (config.mode === 'context') {
      const contextStore = store as any;
      return contextStore
        .query(
          (r: ContextCard) => r.cardType === config.cardType && r.inspectionType === config.inspectionType,
          true // Include filtered records
        )
        .map((r: ContextCard) => ({
          id: String(r.id),
          content: r.content,
          enabled: r.isEnabled,
          cardType: r.cardType,
          inspectionType: r.inspectionType,
          isDraft: r.isDraft
        }));
    } else if (config.mode === 'field-advisor') {
      const fieldAdvisorStore = store as any;
      return fieldAdvisorStore
        .query(
          (r: FieldAdvisorCard) => r.cardType === config.fieldAdvisorCardType && r.advisorType === config.advisorType,
          true // Include filtered records
        )
        .map((r: FieldAdvisorCard) => ({
          id: String(r.id),
          content: r.content,
          enabled: r.isEnabled,
          cardType: r.cardType,
          advisorType: r.advisorType,
          isDraft: r.isDraft
        }));
    } else {
      const promoteStore = store as any;
      return promoteStore
        .query(
          (r: PromotePromptCard) => r.cardType === config.promoteCardType,
          true
        )
        .map((r: PromotePromptCard) => ({
          id: String(r.id),
          content: r.content,
          enabled: r.isEnabled,
          cardType: r.cardType,
          promoteType: config.promoteType,
          isDraft: r.isDraft
        }));
    }
  }, [store, config, version]);

  // Save: add new or update existing via store
  const handleSave = async (id: string, data: { content: string; enabled: boolean }) => {
    if (!canWrite) {
      toast.error("You don't have permission to modify prompt cards");
      return;
    }

    if (!store || !project) return;

    // Prevent disabling the last enabled card in required sections
    if (isRequired && !data.enabled) {
      const enabledCards = cards.filter((c: { enabled: boolean; id: string }) => c.enabled && c.id !== id);
      if (enabledCards.length === 0) {
        toast.warning("At least one card must be enabled in required sections");
        return;
      }
    }

    setSaving(true);

    let recordToAddInStore: ContextCard | FieldAdvisorCard | undefined;

    try {
      const isNewCard = id.startsWith("new-");
      const record = store?.getById(id);

      if (!record || isNewCard) {
        // Add new record
        if (config.mode === 'context') {
          recordToAddInStore = store?.add({
            content: data.content,
            isEnabled: data.enabled,
            cardType: config.cardType,
            inspectionType: config.inspectionType
          })?.[0];
        } else if (config.mode === 'field-advisor') {
          recordToAddInStore = store?.add({
            content: data.content,
            isEnabled: data.enabled,
            cardType: config.fieldAdvisorCardType,
            advisorType: config.advisorType
          })?.[0];
        } else {
          recordToAddInStore = store?.add({
            content: data.content,
            isEnabled: data.enabled,
            cardType: config.promoteCardType
          })?.[0];
        }
      } else {
        // Update existing record
        record.set({
          content: data.content,
          isEnabled: data.enabled
        });
      }

      // Commit changes to backend
      // @ts-expect-error - project type not fully defined
      await project.sync();

      // Set board preference to indicate unpublished changes (show publish button)
      // @ts-expect-error - userPreferences is dynamically set in bryntumContext
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS[publishKey], "true", true);
    } catch (error) {
      if (recordToAddInStore) {
        recordToAddInStore.remove();
        store.remove(recordToAddInStore);
        forceRerender()
      }

      console.error("Error saving prompt card:", error);
    } finally {
      setSaving(false);
    }
  };

  // Delete via store
  const handleDelete = async (id: string, cardsLength: number) => {
    if (!canWrite) {
      toast.error("You don't have permission to delete prompt cards");
      return;
    }

    // Prevent deleting last card if required type
    if (isRequired && cardsLength <= 1) {
      toast.warning("At least one card is required for this section");
      return;
    }

    if (!store || !project) return;

    setSaving(true);

    try {
      const record = store?.getById(id);
      if (record) {
        store?.remove(record);
        // Commit changes to backend
        // @ts-expect-error - project type not fully defined
        await project.sync();

        // Set board preference to indicate unpublished changes (show publish button)
        // @ts-expect-error - userPreferences is dynamically set in bryntumContext
        userPreferences.updateSetting(PERSISTENCE_STATE_KEYS[publishKey], "true", true);
      }
    } catch (error) {
      console.error("Error deleting prompt card:", error);
      toast.error("Failed to delete card. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return {
    cards,
    saving,
    canRead,
    canWrite,
    handleSave,
    handleDelete
  };
};
